import React, { type FC, useCallback, useMemo, useRef, useState } from 'react'

import './_inputField.scss'
import { useTranslation } from 'react-i18next'

import { type IInputField } from 'types/interfaces/IInputField'

const InputField: FC<IInputField> = ({
    id,
    type,
    value,
    label,
    changeFn,
    validate,
    validationCheck,
    validationErrorMessage,
    validationCharactersNumber,
    inputvisible,
    swaptypeicon,
    swaptype,
    additionalProps,
    customClass = '',
    loginAttempted,
    validateEmptyOnBlur = true
}) => {
    const [invalidInput, setInvalidInput] = useState<boolean>(false)
    const [errorMessage, setErrorMessage] = useState<string | null>(null)

    const { t } = useTranslation()

    const inputRef = useRef<HTMLInputElement>(null)
    const validationRules = useMemo(() => validate?.split('|'), [validate])

    const validateInput = useCallback(
        (text: string): void => {
            const validationToggle = (
                isValid: boolean,
                message: string | null
            ) => {
                const validationOverride = validationCheck?.(
                    !isValid,
                    text,
                    message
                )

                if (validationOverride) {
                    isValid = validationOverride.isValid
                    message = validationOverride.isValid
                        ? null
                        : validationOverride.message ??
                          validationErrorMessage ??
                          null
                }

                if (isValid) {
                    setInvalidInput(false)
                    setErrorMessage(null)
                } else {
                    setInvalidInput(true)
                    setErrorMessage(message)
                }
            }

            let isValid: [boolean, string | null] = [true, null]

            validationRules?.every(rule => {
                switch (rule) {
                    case 'required':
                        if (text.length > 0) {
                            isValid = [true, null]
                            return true
                        } else {
                            isValid = [false, t('fieldIsRequired')]
                            return false
                        }

                    case 'only_letters':
                        if (!/^[a-zA-Z\s]*$/.test(text)) {
                            isValid = [false, `${validationErrorMessage}`]
                            return false
                        } else {
                            isValid = [true, null]
                            return true
                        }

                    case 'letters/numbers':
                        if (!/^[a-zA-Z0-9._]*$/.test(text)) {
                            isValid = [false, `${validationErrorMessage}`]
                            return false
                        } else {
                            if (validationCharactersNumber) {
                                if (text.length >= validationCharactersNumber) {
                                    isValid = [true, null]
                                    return true
                                } else {
                                    isValid = [
                                        false,
                                        `${t(
                                            'mustHaveAtLeast'
                                        )} ${validationCharactersNumber} ${t(
                                            'characters'
                                        )}`
                                    ]
                                    return false
                                }
                            } else {
                                isValid = [true, null]
                                return true
                            }
                        }

                    case 'letters/numbers/spaces':
                        if (!/^[a-zA-Z0-9._'\s]*$/.test(text)) {
                            isValid = [false, `${validationErrorMessage}`]
                            return false
                        } else {
                            if (validationCharactersNumber) {
                                if (text.length >= validationCharactersNumber) {
                                    isValid = [true, null]
                                    return true
                                } else {
                                    isValid = [
                                        false,
                                        `${t(
                                            'mustHaveAtLeast'
                                        )} ${validationCharactersNumber} ${t(
                                            'characters'
                                        )}`
                                    ]
                                    return false
                                }
                            } else {
                                isValid = [true, null]
                                return true
                            }
                        }

                    case 'email':
                        if (!/\S+@\S+\.\S+/.test(text) && text.length > 0) {
                            isValid = [false, t('error:errorEmail')]
                            return false
                        } else {
                            isValid = [true, null]
                            return true
                        }

                    case 'password':
                        if (text.length > 64) {
                            isValid = [
                                false,
                                'Password must not exceed 64 characters.'
                            ]
                            return false
                        }

                        if (
                            text.length > 0 &&
                            !/(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.{8,})/.test(text)
                        ) {
                            isValid = [
                                false,
                                validationErrorMessage || ''
                            ]
                            return false
                        }

                        isValid = [true, null]
                        return true

                    default:
                        return true
                }
            })

            validationToggle(isValid[0], isValid[1])
        },
        [
            t,
            validationCheck,
            validationRules,
            validationErrorMessage,
            validationCharactersNumber
        ]
    )

    return (
        <div className={`inputfield ${customClass}`}>
            <label
                className={`inputfield__label ${
                    `${value}`.length > 0 ? 'inputfield__label--active' : ''
                }`}
                htmlFor={id}
            >
                {label}
            </label>
            <input
                ref={inputRef}
                className={`${invalidInput ? 'inputfield--invalid' : ''} ${
                    loginAttempted ? 'inputfield--loginattempted' : ''
                }`}
                id={id}
                type={type}
                value={value}
                onChange={(e: React.FormEvent<HTMLInputElement>): void => {
                    if (errorMessage) {
                        validateInput(e.currentTarget.value)
                    }

                    changeFn(e.currentTarget.value)
                }}
                onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
                    const text = e.currentTarget.value

                    // Allow empty untouched fields without showing errors.
                    if (!validateEmptyOnBlur && text.trim().length === 0) {
                        setInvalidInput(false)
                        setErrorMessage(null)
                        return
                    }

                    validateInput(text)

                    // Preserve existing behavior for other forms.
                    if (!validateEmptyOnBlur) {
                        return
                    }

                    if (
                        text.length === 0 &&
                        validationCharactersNumber
                    ) {
                        setInvalidInput(true)
                        setErrorMessage(
                            `${t('mustHaveAtLeast')} ${validationCharactersNumber} ${t('characters')}`
                        )
                    }

                    if (
                        validationRules?.includes('email') &&
                        text.length === 0
                    ) {
                        setInvalidInput(true)
                        setErrorMessage(t('error:errorEmailBlank'))
                    }
                }}
                onKeyDownCapture={e => {
                    if (
                        type === 'number' &&
                        validationRules?.includes('only_positive')
                    ) {
                        if (
                            (e.keyCode >= 48 && e.keyCode <= 57) ||
                            e.keyCode === 8
                        ) {
                        } else {
                            e.preventDefault()
                        }
                    }
                }}
                {...additionalProps}
            />
            {swaptypeicon && (
                <div
                    className='inputfield__eye__wrapper'
                    onClick={() => {
                        inputRef.current!.focus()
                        swaptype!()
                    }}
                >
                    <div
                        className={`inputfield__eye ${
                            inputvisible ? 'inputfield__eye--visible' : ''
                        }`}
                    />
                </div>
            )}
            {invalidInput && (
                <div
                    className={`inputfield__error${
                        validationErrorMessage
                            ? ' inputfield__error--active'
                            : ''
                    }${
                        validate?.includes('password') &&
                        value.toString().length > 0
                            ? ' inputfield__error--pass'
                            : ''
                    }`}
                >
                    {errorMessage}
                </div>
            )}
        </div>
    )
}

export default InputField
